/**
 * useAgentPermissions - Hook for checking agent permissions
 * Provides granular permission checking for the agent portal
 * 
 * NEW: Agents start with MINIMAL permissions (schedule + clock only)
 * Admins grant additional permissions stored in internal_user_roles.permissions
 */
import { useAuth } from '@/hooks/useAuth';
import { 
  AgentPermission, 
  hasAgentPermission, 
  getAgentPermissions,
  ALL_GRANTABLE_PERMISSIONS,
  PERMISSION_GROUPS,
} from '@/lib/agent/agent-permissions';
import { useMemo, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export function useAgentPermissions() {
  const { user, role, authInfo } = useAuth();
  const [customPermissions, setCustomPermissions] = useState<AgentPermission[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch custom permissions from database
  useEffect(() => {
    async function fetchCustomPermissions() {
      if (!user?.id) {
        setCustomPermissions([]);
        setIsLoading(false);
        return;
      }

      try {
        // Fetch permissions from internal_user_roles
        const { data, error } = await supabase
          .from('internal_user_roles')
          .select('permissions, role')
          .eq('user_id', user.id)
          .eq('is_active', true)
          .maybeSingle();

        if (error) {
          console.error('Error fetching agent permissions:', error);
          setCustomPermissions([]);
        } else if (data?.permissions) {
          // Extract granted permissions from JSON
          const granted = Object.entries(data.permissions as Record<string, boolean>)
            .filter(([_, value]) => value === true)
            .map(([key]) => key as AgentPermission);
          setCustomPermissions(granted);
        } else {
          setCustomPermissions([]);
        }
      } catch (err) {
        console.error('Failed to fetch agent permissions:', err);
        setCustomPermissions([]);
      } finally {
        setIsLoading(false);
      }
    }

    fetchCustomPermissions();
  }, [user?.id]);

  // Get the effective role for permission checking
  const effectiveRole = useMemo(() => {
    // Internal role takes precedence
    if (authInfo?.internal_role) {
      return authInfo.internal_role;
    }
    return role;
  }, [role, authInfo]);

  // Check if user has a specific permission
  const hasPermission = (permission: AgentPermission): boolean => {
    // For agents, include custom permissions
    if (effectiveRole === 'agent') {
      return hasAgentPermission(effectiveRole, permission, customPermissions);
    }
    return hasAgentPermission(effectiveRole, permission);
  };

  // Check if user has any of the specified permissions
  const hasAnyPermission = (permissions: AgentPermission[]): boolean => {
    return permissions.some(p => hasPermission(p));
  };

  // Check if user has all of the specified permissions
  const hasAllPermissions = (permissions: AgentPermission[]): boolean => {
    return permissions.every(p => hasPermission(p));
  };

  // Get all permissions for the current user
  const permissions = useMemo(() => {
    if (effectiveRole === 'agent') {
      return getAgentPermissions(effectiveRole, customPermissions);
    }
    return getAgentPermissions(effectiveRole);
  }, [effectiveRole, customPermissions]);

  return {
    effectiveRole,
    permissions,
    customPermissions,
    isLoading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    // Export for admin UI
    allGrantablePermissions: ALL_GRANTABLE_PERMISSIONS,
    permissionGroups: PERMISSION_GROUPS,
  };
}

// Permission-gated component wrapper
export function PermissionGate({ 
  permission, 
  permissions: requiredPermissions,
  requireAll = false,
  children, 
  fallback = null 
}: { 
  permission?: AgentPermission;
  permissions?: AgentPermission[];
  requireAll?: boolean;
  children: React.ReactNode; 
  fallback?: React.ReactNode;
}): React.ReactNode {
  const { hasPermission, hasAnyPermission, hasAllPermissions, isLoading } = useAgentPermissions();
  
  // Don't render anything while loading permissions
  if (isLoading) {
    return null;
  }
  
  // Check single permission
  if (permission && !hasPermission(permission)) {
    return fallback;
  }
  
  // Check multiple permissions
  if (requiredPermissions) {
    if (requireAll) {
      if (!hasAllPermissions(requiredPermissions)) {
        return fallback;
      }
    } else {
      if (!hasAnyPermission(requiredPermissions)) {
        return fallback;
      }
    }
  }
  
  return children;
}

// Export permission types for use in other components
export type { AgentPermission } from '@/lib/agent/agent-permissions';
